import React, { FC, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  TextField,
  Tooltip,
  DialogContent,
  DialogActions,
} from "@mui/material";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import {
  changeMatchStatus,
  changeMatchTable,
  closeBlockedMatchModal,
  getBlockedMatchModalParams,
  closeOccupiedTableModal,
  getOccupiedTableModalParams,
  closeFreedMatchModal,
  getFreedMatchModalParams,
  getMatchsLoading,
  listenToMatchs,
  forceMatchEnd,
  selectMatchesWithBlockInfo,
} from "../../../features/matchs/matchsSlice";
import { useAppDispatch, useAppSelector } from "../../../store";
import { MatchWithBlockInfo } from "../../../types";
import { getTournoi } from "../../../features/tournoi/tournoiSlice";
import { Tournoi } from "../../../types/Tournoi";

type SortColumn = {
  orderBy: string; // chemin Firestore ou champ à trier
  order: "asc" | "desc";
};

const columns = [
  {
    id: "tableau",
    label: "Tableau",
    orderBy: "tableau.label",
    sortable: true,
  },
  { id: "tour", label: "Tour", orderBy: "tour.label", sortable: true },
  {
    id: "joueur1",
    label: "Joueur 1",
    orderBy: "matchsPlayerParticipations.0.players.name",
    sortable: true,
  },
  {
    id: "joueur2",
    label: "Joueur 2",
    orderBy: "matchsPlayerParticipations.1.players.name",
    sortable: true,
  },
  {
    id: "joueur3",
    label: "Joueur 3",
    orderBy: "matchsPlayerParticipations.2.players.name",
    sortable: true,
  },
  {
    id: "dateCreation",
    label: "Création",
    orderBy: "dateCreation",
    sortable: true,
  },
  {
    id: "dateLancement",
    label: "Lancement",
    orderBy: "dateLancement",
    sortable: true,
  },
  { id: "duree", label: "Durée", orderBy: "dateLancement", sortable: true },
  { id: "statut", label: "Statut", orderBy: "status", sortable: true },
  { id: "tableId", label: "Table", orderBy: "tableId", sortable: true },
  { id: "action", label: "Action", sortable: false },
  { id: "forceEnd", label: "", sortable: false },
];

function getValueByPath(obj: any, path: string) {
  return path.split(".").reduce((acc, part) => {
    if (acc === undefined || acc === null) return undefined;
    // gérer index numérique ex: "matchsPlayerParticipations.0"
    if (/^\d+$/.test(part)) {
      return Array.isArray(acc) ? acc[parseInt(part, 10)] : undefined;
    }
    return acc[part];
  }, obj);
}

function compareValues(a: any, b: any): number {
  // Regrouper undefined/null à la fin, les considérer égaux entre eux
  const aIsNil = a === undefined || a === null;
  const bIsNil = b === undefined || b === null;

  if (aIsNil && bIsNil) return 0; // égaux entre eux => passer au critère suivant
  if (aIsNil) return 1; // a va après b
  if (bIsNil) return -1; // a avant b

  // Sinon comparer normalement selon le type
  if (typeof a === "string" && typeof b === "string") {
    return a.localeCompare(b, undefined, { sensitivity: "base" });
  }
  if (typeof a === "number" && typeof b === "number") {
    return a - b;
  }
  // Ajoute d'autres types si besoin

  // Cas générique
  if (a > b) return 1;
  if (a < b) return -1;
  return 0;
}

function multiSort(
  a: any,
  b: any,
  sortCriteria: { path: string; direction: "asc" | "desc" }[]
) {
  for (const { path, direction } of sortCriteria) {
    const valA = getValueByPath(a, path);
    const valB = getValueByPath(b, path);
    const cmp = compareValues(valA, valB);
    if (cmp !== 0) {
      return direction === "asc" ? cmp : -cmp;
    }
    // si cmp === 0, passer au critère suivant
  }
  return 0;
}

// Helper pour savoir si un match doit être alerté et le type d'alerte
function getMatchAlertInfo(
  match: MatchWithBlockInfo,
  alertThresholds: Tournoi["alertThresholds"]
): null | { type: "cree" | "enCours"; minutes: number; seuil: number } {
  if (!alertThresholds) return null;
  const nbJoueurs = match.matchsPlayerParticipations?.length;
  if (nbJoueurs !== 2 && nbJoueurs !== 3) return null;
  const nbJoueursKey = String(nbJoueurs ?? "");
  const now = Date.now();
  if (["CREE", "DOUBLON", "LIBERE"].includes(match.status || "")) {
    const seuil = alertThresholds.cree?.[nbJoueursKey];
    if (!seuil || !match.dateCreation) return null;
    const minutes = (now - match.dateCreation.getTime()) / 60000;
    if (minutes > seuil)
      return { type: "cree", minutes: Math.floor(minutes), seuil };
    return null;
  }
  if ((match.status || "") === "ENCOURS") {
    const seuil = alertThresholds.enCours?.[nbJoueursKey];
    if (!seuil || !match.dateLancement) return null;
    const minutes = (now - match.dateLancement.getTime()) / 60000;
    if (minutes > seuil)
      return { type: "enCours", minutes: Math.floor(minutes), seuil };
    return null;
  }
  return null;
}

const MatchsList: FC = () => {
  const dispatch = useAppDispatch();

  const matchs = useAppSelector(selectMatchesWithBlockInfo);
  const loading = useAppSelector(getMatchsLoading);
  const blockedMatchModalParams = useAppSelector(getBlockedMatchModalParams);
  const occupiedTableModalParams = useAppSelector(getOccupiedTableModalParams);
  const freedMatchModalParams = useAppSelector(getFreedMatchModalParams);
  const tournoi = useAppSelector(getTournoi);
  const defaultAlertThresholds = {
    cree: { "2": 60, "3": 20 },
    enCours: { "2": 50, "3": 100 },
  };
  const alertThresholds = {
    cree: {
      ...defaultAlertThresholds.cree,
      ...(tournoi?.alertThresholds?.cree || {}),
    },
    enCours: {
      ...defaultAlertThresholds.enCours,
      ...(tournoi?.alertThresholds?.enCours || {}),
    },
  };
  // DEBUG TEMP
  console.log("alertThresholds utilisés dans MatchsList:", alertThresholds);

  const [sortColumns, setSortColumns] = React.useState<SortColumn[]>([]);

  const sortedMatchs = React.useMemo(() => {
    if (sortColumns.length === 0) return matchs;

    // Transformer SortColumn[] en { path, direction }[]
    const sortCriteria = sortColumns.map(({ orderBy, order }) => ({
      path: orderBy,
      direction: order,
    }));

    return [...matchs].sort((a, b) => multiSort(a, b, sortCriteria));
  }, [matchs, sortColumns]);

  const handleRequestSort = (property: string) => {
    // Trouve la colonne par son id
    const column = columns.find((col) => col.id === property);
    if (!column || !column.orderBy) return;

    setSortColumns((prev) => {
      const existingIndex = prev.findIndex((c) => c.orderBy === column.orderBy);
      if (existingIndex === -1) {
        // Ajoute la colonne en ascendant par défaut
        return [...prev, { orderBy: column.orderBy, order: "asc" }];
      } else {
        // Inverse l'ordre ou supprime la colonne si déjà desc
        const updated = [...prev];
        const currentOrder = updated[existingIndex].order;
        if (currentOrder === "asc") {
          updated[existingIndex].order = "desc";
          return updated;
        } else {
          // supprime la colonne du tri si on clique une 2e fois en desc
          updated.splice(existingIndex, 1);
          return updated;
        }
      }
    });
  };

  const getSortDirection = (col: (typeof columns)[number]) => {
    if (!col.sortable || !col.orderBy) return false;
    const colSort = sortColumns.find((c) => c.orderBy === col.orderBy);
    return colSort ? colSort.order : false;
  };

  useEffect(() => {
    // Démarrage du listener temps réel
    const unsubscribe = dispatch(listenToMatchs());
    return () => unsubscribe();
  }, [dispatch]);

  const [tableInputs, setTableInputs] = React.useState<Record<string, string>>(
    {}
  );
  const [matchToForceEnd, setMatchToForceEnd] =
    React.useState<MatchWithBlockInfo | null>(null);

  const getPlayerName = (match: MatchWithBlockInfo, index: number) => {
    if (!match || !match.matchsPlayerParticipations || index < 0 || index > 3) {
      return "";
    }
    const player = match.matchsPlayerParticipations?.[index]?.players;
    return player ? `${player.name} ${player.surname}` : "";
  };

  const isPlayerBlocking = (
    match: MatchWithBlockInfo,
    playerId: string
  ): boolean => {
    if (!playerId) return false;
    // Si match en doublon, check si joueur est bloquant
    if (match.status === "DOUBLON" && match.blockedByPlayers) {
      if (match.blockedByPlayers.some((bp) => bp.player.id === playerId)) {
        return true;
      }
    }

    // Si match en cours, check si joueur bloque un autre match (bloquéPlayers)
    if (match.status === "ENCOURS" && match.blockedPlayers) {
      if (match.blockedPlayers.some((bp) => bp.player.id === playerId)) {
        return true;
      }
    }

    return false;
  };

  const getMatchDuration = (match: MatchWithBlockInfo) => {
    if (match.status !== "ENCOURS" || !match.dateLancement) return "";
    const now = Date.now();
    const launch = match.dateLancement.getTime();
    return `${Math.floor((now - launch) / 1000 / 60)} min`;
  };

  const handleTableChangeLocal = (matchId: string, val: string) => {
    // Autoriser vide pour vider le champ
    if (val === "") {
      setTableInputs((prev) => ({ ...prev, [matchId]: "" }));
      return;
    }
    // Accepter uniquement entiers > 0
    const intVal = parseInt(val, 10);
    if (!isNaN(intVal) && intVal > 0) {
      setTableInputs((prev) => ({ ...prev, [matchId]: intVal.toString() }));
    }
  };

  const handleTableChangeCommit = (match: MatchWithBlockInfo) => {
    const val = tableInputs[match.id!] ?? "";
    if (val === "") return; // ne pas envoyer si vide
    const intVal = parseInt(val, 10);
    if (!isNaN(intVal) && intVal > 0 && intVal !== match.tableId) {
      dispatch(
        changeMatchTable({
          matchId: match.id!,
          tableId: intVal,
        })
      );
    }
  };

  return (
    <>
      {loading ? (
        <Box display="flex" flexDirection="column" alignItems="center" p={2}>
          <CircularProgress />
          <Typography>Chargement des matchs...</Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            width: "100%",
            maxHeight: "calc(100vh - 280px)",
            overflow: "auto",
            marginBottom: 2,
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map(({ id, label, sortable }) => (
                  <TableCell
                    key={id}
                    onClick={() => sortable && handleRequestSort(id)}
                    sx={{
                      cursor: sortable ? "pointer" : "default",
                      userSelect: "none",
                    }}
                    sortDirection={
                      getSortDirection(columns.find((c) => c.id === id)!) ||
                      false
                    }
                  >
                    <Typography
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        userSelect: "none",
                      }}
                      variant="body2"
                    >
                      {label}
                      {(() => {
                        const sortDir = getSortDirection(
                          columns.find((c) => c.id === id)!
                        );
                        if (!sortDir) return null;
                        return sortDir === "asc" ? " ▲" : " ▼";
                      })()}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedMatchs.map((match) => {
                return (
                  <TableRow
                    key={match.id}
                    sx={{
                      backgroundColor:
                        match.status === "DOUBLON"
                          ? "#ffccff"
                          : match.status === "LIBERE"
                          ? "#b3f0ff"
                          : match.status === "ENCOURS"
                          ? "#b3ffb3"
                          : "white",
                      "&:hover": {
                        backgroundColor: "#eaeaea",
                      },
                    }}
                  >
                    <TableCell>
                      {match.tableau?.name && match.tableau?.label
                        ? `${match.tableau.name} - ${match.tableau.label}`
                        : match.tableau?.label ?? ""}
                    </TableCell>
                    <TableCell>{match.tour?.label ?? ""}</TableCell>
                    {[0, 1, 2].map((i) => {
                      const playerParticipation =
                        match.matchsPlayerParticipations?.[i];
                      const playerName = playerParticipation?.players
                        ? `${playerParticipation.players.name} ${playerParticipation.players.surname}`
                        : "";
                      const isBlocking =
                        playerParticipation &&
                        isPlayerBlocking(match, playerParticipation.playersId);
                      return (
                        <TableCell
                          key={i}
                          sx={{ color: isBlocking ? "red" : "inherit" }}
                        >
                          {playerName}
                        </TableCell>
                      );
                    })}
                    <TableCell>
                      {match.dateCreation?.toLocaleTimeString("fr-FR")}
                    </TableCell>
                    <TableCell>
                      {match.dateLancement?.toLocaleTimeString("fr-FR")}
                    </TableCell>
                    <TableCell>{getMatchDuration(match)}</TableCell>
                    <TableCell>
                      {(() => {
                        const alertInfo = getMatchAlertInfo(
                          match,
                          alertThresholds
                        );
                        if (!alertInfo) return null;
                        let message = "";
                        if (alertInfo.type === "cree") {
                          message = `Ce match n'a pas été lancé depuis plus de ${alertInfo.seuil} min (${alertInfo.minutes} min)`;
                        } else {
                          message = `Ce match est en cours depuis plus de ${alertInfo.seuil} min (${alertInfo.minutes} min)`;
                        }
                        return (
                          <Tooltip title={message}>
                            <WarningAmberOutlinedIcon
                              color="warning"
                              sx={{ mr: 0.5, verticalAlign: "middle" }}
                            />
                          </Tooltip>
                        );
                      })()}
                      {match.status === "DOUBLON" && match.blockedByPlayers ? (
                        <Tooltip
                          title={
                            <List sx={{ p: 1, m: 0 }}>
                              {match.blockedByPlayers.map(
                                ({ player, match }, i) => (
                                  <ListItem key={i} sx={{ fontSize: "0.8rem" }}>
                                    {player.name} {player.surname} bloque sur la
                                    table {match.tableId}
                                  </ListItem>
                                )
                              )}
                            </List>
                          }
                          arrow
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              cursor: "help",
                              color: "red",
                              fontWeight: "bold",
                              gap: 0.5, // espace entre texte et picto
                              userSelect: "none",
                            }}
                          >
                            <Typography component="span">DOUBLON</Typography>
                            <Typography
                              component="span"
                              aria-label="Warning icon"
                              role="img"
                            >
                              ⚠️
                            </Typography>
                          </Box>
                        </Tooltip>
                      ) : (
                        match.status
                      )}
                    </TableCell>

                    <TableCell
                      sx={{
                        padding: "4px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      <TextField
                        size="small"
                        type="number"
                        value={
                          tableInputs[match.id!] ??
                          match.tableId?.toString() ??
                          ""
                        }
                        onChange={(e) =>
                          handleTableChangeLocal(match.id!, e.target.value)
                        }
                        onBlur={() => handleTableChangeCommit(match)}
                        inputProps={{
                          min: 1,
                          step: 1,
                          style: { textAlign: "center", padding: "4px 6px" },
                        }}
                        sx={{
                          width: "4rem",
                          height: "32px",
                          padding: "0 6px",
                          fontSize: "0.875rem",
                          outline: "none",
                          textAlign: "center",
                          backgroundColor: "inherit", // hérite de la couleur de fond de la ligne
                          lineHeight: "32px",
                          boxShadow: "none",
                          marginBottom: "0px",
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "4px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      <Button
                        className="match-action-button"
                        variant="contained"
                        size="small"
                        color={
                          match.status === "ENCOURS" ? "secondary" : "primary"
                        }
                        disabled={match.status !== "ENCOURS" && !match.tableId}
                        onClick={() => dispatch(changeMatchStatus(match))}
                        sx={{
                          "&.match-action-button::after": { display: "none" },
                          "&.match-action-button:hover::after": {
                            display: "none",
                          },
                          "&.match-action-button:hover": {
                            backgroundColor:
                              match.status === "ENCOURS"
                                ? (theme) => theme.palette.secondary.main
                                : (theme) => theme.palette.primary.main,
                            color:
                              match.status === "ENCOURS"
                                ? (theme) => theme.palette.text.primary
                                : (theme) => theme.palette.primary.contrastText,
                            boxShadow: "none",
                            filter: "none",
                            transform: "none !important",
                          },
                          "&.match-action-button:focus-visible": {
                            outline: "none",
                            boxShadow: "none",
                          },
                          "&.match-action-button:active": {
                            backgroundColor:
                              match.status === "ENCOURS"
                                ? (theme) => theme.palette.secondary.main
                                : (theme) => theme.palette.primary.main,
                            boxShadow: "none",
                            transform: "none !important",
                          },
                        }}
                      >
                        {match.status === "ENCOURS" ? "TERMINER" : "LANCER"}
                      </Button>
                    </TableCell>
                    <TableCell>
                      {match.status !== "ENCOURS" && (
                        <StopCircleOutlinedIcon
                          color="error"
                          fontSize="small"
                          sx={{ cursor: "pointer" }}
                          onClick={() => setMatchToForceEnd(match)}
                          titleAccess="Forcer la fin du match"
                        />
                      )}
                      {match.bloquant && (
                        <Tooltip title="Match bloquant" arrow placement="top">
                          <BlockOutlinedIcon
                            color="error"
                            fontSize="small"
                            sx={{ cursor: "pointer" }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialogs */}
      <Dialog
        open={!!blockedMatchModalParams}
        onClose={() => dispatch(closeBlockedMatchModal())}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberOutlinedIcon color="warning" />
          <Typography variant="h6">Conflit de joueurs détecté</Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Typography variant="body1" gutterBottom>
            Le match que vous venez de lancer a bien été créé.
            <br />
            <strong>
              Cependant, les matchs suivants sont désormais en doublon à cause
              de conflits de joueurs&nbsp;:
            </strong>
          </Typography>
          <List dense>
            {(blockedMatchModalParams?.blockingPlayers ?? []).map(
              (bp: any, idx: number) => {
                const match = bp.match;
                const joueurs = match.matchsPlayerParticipations
                  .map((mpp: any) =>
                    mpp.players
                      ? `${mpp.players.surname} ${mpp.players.name}`
                      : ""
                  )
                  .filter(Boolean)
                  .join(", ");
                return (
                  <ListItem
                    key={match.id}
                    disableGutters
                    sx={{
                      flexDirection: "column",
                      alignItems: "flex-start",
                      mb: 1,
                      width: "100%",
                    }}
                  >
                    <ListItemText
                      primary={
                        <>
                          {match.tableId != null && match.tableId !== "" ? (
                            <>
                              <strong>Table {match.tableId}</strong> —{" "}
                              <span
                                style={{ color: "#b71c1c", fontWeight: 600 }}
                              >
                                DOUBLON
                              </span>
                            </>
                          ) : (
                            <span style={{ color: "#b71c1c", fontWeight: 600 }}>
                              DOUBLON
                            </span>
                          )}
                        </>
                      }
                      secondary={
                        <>
                          <span>Joueurs du match bloqué : {joueurs}</span>
                        </>
                      }
                      primaryTypographyProps={{ fontWeight: "bold" }}
                      secondaryTypographyProps={{
                        fontSize: "0.95rem",
                        color: "text.secondary",
                      }}
                    />
                  </ListItem>
                );
              }
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => dispatch(closeBlockedMatchModal())}
            color="primary"
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!occupiedTableModalParams}
        onClose={() => dispatch(closeOccupiedTableModal())}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <BlockOutlinedIcon color="error" />
          <Typography variant="h6">Table occupée</Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Typography variant="body1">
            La table <strong>{occupiedTableModalParams?.blockedTable}</strong>{" "}
            est déjà occupée. Veuillez terminer le match en cours avant d'en
            lancer un nouveau.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => dispatch(closeOccupiedTableModal())}
            color="primary"
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!freedMatchModalParams}
        onClose={() => dispatch(closeFreedMatchModal())}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CheckCircleOutlineIcon color="success" />
          <Typography variant="h6">Joueurs libérés</Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Typography variant="body1" gutterBottom>
            Les joueurs suivants sont désormais disponibles :
          </Typography>
          <List dense>
            {freedMatchModalParams?.freedPlayers.map((p, idx) => (
              <ListItem key={idx} disableGutters>
                <ListItemText
                  primary={`${p.surname} ${p.name}`}
                  primaryTypographyProps={{ fontWeight: "medium" }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => dispatch(closeFreedMatchModal())}
            color="primary"
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!matchToForceEnd}
        onClose={() => setMatchToForceEnd(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberOutlinedIcon color="warning" />
          <Typography variant="h6">Confirmer la fin du match</Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Typography variant="body1">
            Êtes-vous sûr de vouloir forcer la fin du match entre{" "}
            <strong>{getPlayerName(matchToForceEnd!, 0)}</strong> et{" "}
            <strong>{getPlayerName(matchToForceEnd!, 1)}</strong> ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMatchToForceEnd(null)}>Annuler</Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              if (!matchToForceEnd) return;
              await dispatch(forceMatchEnd({ matchId: matchToForceEnd.id! }));
              setMatchToForceEnd(null);
            }}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MatchsList;
